package dev.lingvo.shared

import kotlinx.serialization.Serializable

/** What a terminal asks for when it starts a pairing. */
@Serializable
data class DeviceAuthorizationRequest(
    /** Self-reported, and therefore untrusted. Shown to the approver as escaped text, never markup. */
    val clientName: String? = null,
    /** The machine's own name, so somebody can recognise it — or fail to, which is the point. */
    val machineLabel: String? = null,
    val scopes: List<TokenScope> = emptyList(),
)

/** What the terminal prints and then polls with. */
@Serializable
data class DeviceAuthorizationDto(
    /** Held by the terminal and never shown to anybody. Only its hash is stored. */
    val deviceCode: String,
    /** Eight characters the person reads off one screen and types into another. */
    val userCode: String,
    val verificationUrl: String,
    val expiresInSeconds: Int,
    /** How often to poll. The server answers `slow_down` if the terminal ignores it. */
    val intervalSeconds: Int = 5,
)

/** Where a pairing has got to. */
@Serializable
enum class DeviceAuthorizationState { PENDING, APPROVED, DENIED, EXPIRED }

/**
 * What the browser shows before somebody approves.
 *
 * Every field here exists so a person can recognise a pairing that is not theirs. This flow is
 * shaped exactly like a consent-phishing attack — an attacker starts a pairing and sends the victim
 * the code — and the only defence that works at the moment of approval is a screen that says which
 * machine, from which address, asking for what.
 */
@Serializable
data class DeviceApprovalDto(
    val userCode: String,
    val clientName: String? = null,
    val machineLabel: String? = null,
    val requestedIp: String? = null,
    val requestedScopes: List<TokenScope> = emptyList(),
    /**
     * The scopes that will actually be granted.
     *
     * When a request asks for more than the person's role allows, the excess is shown struck through
     * and the intersection is granted — **and said out loud**. Silently granting less is how a CI
     * job breaks a week later for no visible reason.
     */
    val grantedScopes: List<TokenScope> = emptyList(),
    val expiresInSeconds: Int,
)

/** The poll response. The token appears exactly once, and only here. */
@Serializable
data class DevicePollDto(
    val state: DeviceAuthorizationState,
    /** Present only on the first successful poll after approval. Never rendered in a browser. */
    val token: String? = null,
    val scopes: List<TokenScope> = emptyList(),
)
